// Client-side reactive demo overlay. When ON, cost figures are shown x10 and three
// mock accounts (Alpha/Beta/Gamma) are injected — purely in the browser, no server
// involvement. Toggling is instant (no redeploy). Activate via the header Demo button
// or the URL (?demo=1 on / ?demo=0 off). REAL data is untouched; this only reshapes
// the fetched response before it reaches the pages.
import type { CostBreakdownItem, CostsResponse } from "../api/types.js";

const KEY = "jammsante_monitoring_demo";
const MULT = 10;

const MOCK = [
  { key: "alpha", label: "Alpha", mtd: 214.6, prev: 198.3 },
  { key: "beta", label: "Beta", mtd: 138.2, prev: 121.7 },
  { key: "gamma", label: "Gamma", mtd: 91.4, prev: 88.0 },
];
const mockMtd = MOCK.reduce((s, a) => s + a.mtd, 0);
const mockPrev = MOCK.reduce((s, a) => s + a.prev, 0);

export function isDemoOn(): boolean {
  return sessionStorage.getItem(KEY) === "1";
}

export function setDemoOn(on: boolean): void {
  if (on) sessionStorage.setItem(KEY, "1");
  else sessionStorage.removeItem(KEY);
}

// ?demo=1 activates on load (link-shareable); ?demo=0 forces off.
export function initDemoFromUrl(): void {
  const flag = new URLSearchParams(window.location.search).get("demo");
  if (flag === "1") setDemoOn(true);
  else if (flag === "0") setDemoOn(false);
}

interface DailyLike {
  days: string[];
  series: { account: string; label: string; amounts: number[] }[];
  generatedAt?: string;
}

const scaleItems = (items: CostBreakdownItem[]): CostBreakdownItem[] =>
  items.map((i) => ({ ...i, amount: i.amount * MULT }));

function demoCosts(r: CostsResponse): CostsResponse {
  const mtd = r.monthToDateTotal * MULT + mockMtd;
  const prev = r.previousMonthTotal * MULT + mockPrev;
  const momDelta = mtd - prev;
  const byAccount = [
    ...scaleItems(r.byAccount),
    ...MOCK.map((a) => ({ key: a.key, label: a.label, amount: a.mtd })),
  ].sort((a, b) => b.amount - a.amount);
  return {
    ...r,
    monthToDateTotal: mtd,
    previousMonthTotal: prev,
    grossMonthToDate: r.grossMonthToDate * MULT + mockMtd,
    grossPreviousMonth: r.grossPreviousMonth * MULT + mockPrev,
    momDelta,
    momDeltaPct: prev > 0 ? (momDelta / prev) * 100 : r.momDeltaPct,
    forecastMonthEnd:
      r.forecastMonthEnd === null ? null : r.forecastMonthEnd * MULT + mockMtd * 4.2,
    byAccount,
    byService: scaleItems(r.byService),
  };
}

function demoDaily(r: DailyLike): DailyLike {
  const scaled = r.series.map((s) => ({ ...s, amounts: s.amounts.map((a) => a * MULT) }));
  const mock = MOCK.map((a, idx) => {
    const base = a.mtd / 8;
    const amounts = r.days.map((_, i) =>
      Number((base * (0.65 + ((i * 7 + idx * 13) % 12) / 16)).toFixed(2)),
    );
    return { account: a.key, label: a.label, amounts };
  });
  return { ...r, series: [...scaled, ...mock] };
}

// Reshape a fetched response by endpoint path when demo is on; identity otherwise.
export function applyDemo<T>(path: string, data: T): T {
  if (!isDemoOn()) return data;
  if (path === "/api/costs") return demoCosts(data as CostsResponse) as T;
  if (path === "/api/costs/daily") return demoDaily(data as DailyLike) as T;
  return data;
}
